import * as THREE from "three";
import { critterGround } from "./critterTuning";

/** A soft dark disc (full at the centre, gone at the rim) for the ground shadows of the flock and the critters. */
export function softDisc(): THREE.CanvasTexture {
  const cv = document.createElement("canvas");
  cv.width = cv.height = 32;
  const g = cv.getContext("2d")!;
  const grad = g.createRadialGradient(16, 16, 0, 16, 16, 16);
  grad.addColorStop(0, "rgba(0,0,0,1)"); grad.addColorStop(0.5, "rgba(0,0,0,0.6)"); grad.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = grad; g.fillRect(0, 0, 32, 32);
  return new THREE.CanvasTexture(cv);
}

/**
 * A flock of birds circling at altitude (terrain F9, the "living meadow"): 6–9 flapping card birds in one loose arc
 * round a slowly wandering centre, and — the part that sells it — a soft shadow for each, sliding over the ground and
 * across the road where the sun would put it. From the play camera the birds themselves are small; their shadows
 * crossing the map are what reads as life and as height.
 *
 * Three instanced meshes for the birds (body, left wing, right wing: 8 triangles a bird) and one for the shadows:
 * four draws and no per-frame allocation. Everything is updated by matrix.
 */
export class Flock {
  readonly group = new THREE.Group();
  private readonly n: number;
  private readonly body: THREE.InstancedMesh;
  private readonly wingL: THREE.InstancedMesh;
  private readonly wingR: THREE.InstancedMesh;
  private readonly shadows: THREE.InstancedMesh;
  private readonly phase: Float32Array;
  private readonly lane: Float32Array;
  private t = Math.random() * 100;
  private sun: THREE.DirectionalLight | null = null;
  private sunLooked = false;
  // scratch, so the per-frame update allocates nothing
  private readonly m = new THREE.Matrix4();
  private readonly w = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly e = new THREE.Euler(0, 0, 0, "YXZ");
  private readonly p = new THREE.Vector3();
  private readonly s = new THREE.Vector3(1, 1, 1);
  private readonly d = new THREE.Vector3();
  /** Height the flock circles at, in world units (a tower is ~2). */
  static readonly Altitude = 4;
  /** Wingspan of one bird: a big bird, a crow or a kite, at the map's scale. */
  static readonly Span = 0.26;
  /** Seconds for the flock to go once round. */
  static readonly Lap = 38;

  constructor(private readonly cx: number, private readonly cz: number, private readonly radius: number) {
    this.n = 6 + Math.floor(Math.random() * 4);
    const mat = new THREE.MeshStandardMaterial({ color: 0x3b2c22, roughness: 1, metalness: 0, side: THREE.DoubleSide });
    const S = Flock.Span / 2;
    // body: a slim diamond card, beak to tail, plus a small tail fan
    const body = new THREE.BufferGeometry();
    body.setAttribute("position", new THREE.Float32BufferAttribute([
      0, 0, 0.09, 0.022, 0, 0, 0, 0.012, 0,   0, 0, 0.09, 0, 0.012, 0, -0.022, 0, 0,
      0.022, 0, 0, 0, 0, -0.06, 0, 0.012, 0,   0, 0.012, 0, 0, 0, -0.06, -0.022, 0, 0,
      0, 0, -0.05, 0.03, 0, -0.1, -0.03, 0, -0.1,
    ], 3));
    body.computeVertexNormals();
    // wing: root along the body, swept tip; the right wing is the same card mirrored by its matrix
    const wing = new THREE.BufferGeometry();
    wing.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0.035, S, 0, -0.03, 0, 0, -0.03, S, 0, -0.03, S * 0.7, 0, -0.07, 0, 0, -0.03], 3));
    wing.computeVertexNormals();
    this.body = new THREE.InstancedMesh(body, mat, this.n);
    this.wingL = new THREE.InstancedMesh(wing, mat, this.n);
    this.wingR = new THREE.InstancedMesh(wing, mat, this.n);
    // the shadow: a soft dark disc, stretched along the flight line
    const shadowMat = new THREE.MeshBasicMaterial({ map: softDisc(), transparent: true, opacity: 0.2, depthWrite: false, toneMapped: false });
    const plane = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.shadows = new THREE.InstancedMesh(plane, shadowMat, this.n);
    this.shadows.renderOrder = 1;
    for (const im of [this.body, this.wingL, this.wingR, this.shadows]) {
      im.castShadow = false; im.receiveShadow = false;
      im.frustumCulled = false;                           // the instances roam the whole map; bounds would go stale
      im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.group.add(im);
    }
    this.phase = Float32Array.from({ length: this.n }, () => Math.random() * Math.PI * 2);
    this.lane = Float32Array.from({ length: this.n }, () => Math.random());
  }

  update(dt: number, parent: THREE.Object3D): void {
    this.t += dt;
    const t = this.t;
    if (!this.sunLooked) {
      // the key light rides the camera (look.ts placeSun), so its direction is read every frame from the light itself
      let root: THREE.Object3D = parent;
      while (root.parent) root = root.parent;
      root.traverse((o) => { if (!this.sun && (o as THREE.DirectionalLight).isDirectionalLight) this.sun = o as THREE.DirectionalLight; });
      this.sunLooked = root !== parent;
    }
    // the ground offset of a shadow per unit of height: along the sun, flattened
    let ox = 0, oz = 0;
    if (this.sun) {
      this.d.copy(this.sun.position).sub(this.sun.target.position);
      if (this.d.y > 0.2) { ox = -this.d.x / this.d.y; oz = -this.d.z / this.d.y; }
    }
    // the circle's centre wanders over the map, so the flock is not a merry-go-round
    const ccx = this.cx + Math.sin(t * 0.021) * this.radius * 0.6, ccz = this.cz + Math.cos(t * 0.017) * this.radius * 0.6;
    const base = (t / Flock.Lap) * Math.PI * 2;
    for (let i = 0; i < this.n; i++) {
      const ph = this.phase[i], lane = this.lane[i];
      const a = base + i * 0.32 + Math.sin(t * 0.3 + ph) * 0.06;         // a loose arc, not a ring
      const r = this.radius * (0.85 + lane * 0.3);
      const x = ccx + Math.cos(a) * r, z = ccz + Math.sin(a) * r;
      const y = Flock.Altitude + lane * 0.8 + Math.sin(t * 0.7 + ph) * 0.15;
      // heading: along the circle (counter-clockwise tangent), banked into the turn
      const yaw = Math.atan2(-Math.sin(a), Math.cos(a));
      this.e.set(0, yaw, -0.28);
      this.q.setFromEuler(this.e);
      this.p.set(x, y, z);
      this.s.set(1, 1, 1);
      this.m.compose(this.p, this.q, this.s);
      this.body.setMatrixAt(i, this.m);
      // flap in bursts and glide between them, each bird on its own clock
      const flapping = Math.sin(t * 0.55 + ph * 3) > -0.2;
      const flap = flapping ? Math.sin(t * 11 + ph) * 0.75 : 0.12;
      this.w.makeRotationZ(flap);
      this.wingL.setMatrixAt(i, this.w.premultiply(this.m));
      this.w.makeRotationZ(-flap);
      this.w.premultiply(this.m);
      this.w.multiply(Flock.Mirror);
      this.wingR.setMatrixAt(i, this.w);
      // shadow: where the sun puts it on the ground, fading a little with the bird's height
      const hx = x + ox * y, hz = z + oz * y;
      this.p.set(hx, critterGround(hx, hz) + 0.03, hz);
      this.e.set(0, yaw, 0);
      this.q.setFromEuler(this.e);
      this.s.set(Flock.Span * 1.1, 1, Flock.Span * 0.8);
      this.m.compose(this.p, this.q, this.s);
      this.shadows.setMatrixAt(i, this.m);
    }
    this.body.instanceMatrix.needsUpdate = true;
    this.wingL.instanceMatrix.needsUpdate = true;
    this.wingR.instanceMatrix.needsUpdate = true;
    this.shadows.instanceMatrix.needsUpdate = true;
  }
  private static readonly Mirror = new THREE.Matrix4().makeScale(-1, 1, 1);

  dispose(): void {
    for (const im of [this.body, this.wingL, this.wingR, this.shadows]) { im.geometry.dispose(); im.dispose(); }
    (this.shadows.material as THREE.MeshBasicMaterial).map?.dispose();
    (this.shadows.material as THREE.Material).dispose();
    (this.body.material as THREE.Material).dispose();
    this.group.clear();
  }
}
